import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Callout, Card, Heading, Text } from '../ui/index.js';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { Focusable } from '../components/focus/Focusable.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatBytes, formatRelative } from '../lib/format.js';

export const Route = createFileRoute('/admin/')({ component: AdminOverview });

interface Overview {
  membersCount: number;
  activeCrews: number;
  failedWebhooks: number;
  pendingTasks: Array<{ id: string; action: string; note: string; createdAt: number }>;
  lastBuilds: Array<{
    id: string;
    version_label: string;
    status: string;
    platform: string;
    total_bytes: number | null;
    new_bytes: number | null;
    created_at: number;
  }>;
}

function AdminOverview() {
  const qc = useQueryClient();
  const { isAdmin } = useAccess();

  const { data, isLoading } = useQuery({
    queryKey: keys.admin.overview,
    queryFn: () => api.get<Overview>('/admin'),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/tasks/${id}/resolve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.overview }),
  });

  if (!isAdmin) return <EmptyState title="You cannot do that." />;
  if (isLoading || !data) return <Loading />;

  const stats = [
    { label: 'Members', value: data.membersCount },
    { label: 'Active crews', value: data.activeCrews },
    { label: 'Failed webhooks', value: data.failedWebhooks },
  ];

  return (
    <div className="gb-stack">
      <div className="gb-grid">
        {stats.map((stat) => (
          <Card key={stat.label} size="3">
            <Text size="2" color="gray">
              {stat.label}
            </Text>
            <Heading size="7">{stat.value}</Heading>
          </Card>
        ))}
      </div>

      {/* Work no API can finish for us, mostly the unused-days refund after an upgrade
          (VERIFY-2). */}
      {data.pendingTasks.length > 0 ? (
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">{'Things to do by hand'}</Heading>
          {data.pendingTasks.map((task) => (
            <Callout.Root key={task.id} color="amber">
              <Callout.Description>
                {task.note}
                <br />
                <Text size="1" color="gray">
                  {formatRelative(task.createdAt)}
                </Text>
              </Callout.Description>
              <Focusable onSelect={() => resolve.mutate(task.id)} selectLabel="Mark done">
                <Button size="1" variant="surface">
                  {'Mark done'}
                </Button>
              </Focusable>
            </Callout.Root>
          ))}
        </Card>
      ) : null}

      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'Last builds'}</Heading>
        {data.lastBuilds.length === 0 ? (
          <Text color="gray">{'None'}</Text>
        ) : (
          data.lastBuilds.map((build) => (
            <div key={build.id} className="gb-seat">
              <Text size="2">{build.version_label}</Text>
              <Text size="1" color="gray">
                {build.platform} · {build.status}
              </Text>
              <span className="gb-seat__spacer" />
              <Text size="1" color="gray">
                {build.total_bytes ? formatBytes(build.total_bytes) : ''}
                {build.new_bytes ? ` · ${formatBytes(build.new_bytes)} new` : ''}
              </Text>
              <Text size="1" color="gray">
                {formatRelative(build.created_at)}
              </Text>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
